/**
 * Catálogo del cotizador de Pymio (marketplace), tomado tal cual del prototipo (`pymio/pymio-productos.jsx`).
 *
 * TODO: son datos mock. Los rangos, montos por default y productos tienen que salir de la config del tax system
 * cuando se conecte la lógica (hoy el backend cotiza un solo tax system por solicitud y aquí se piden varios).
 */

export type FlujoPymioId = "liquidez" | "activo";
export type ProductoPymioId = "credito" | "credito_gar" | "factoraje" | "tpv" | "arrendamiento";

/** Cómo se cotiza: cambia el plazo (meses o días), la fórmula y los textos de la tarjeta de resultado. */
export type ModoCotizadorPymio = "liquidez" | "factoraje" | "tpv" | "activo";

interface RangoMonto {
  min: number;
  max: number;
  step: number;
  /** Monto con el que arranca el slider. */
  monto: number;
}

/** Textos propios de un producto cuando es el único de la solicitud (factoraje y TPV). */
interface TextosProducto {
  modo: ModoCotizadorPymio;
  eyebrow: string;
  titulo: string;
  subtitulo: string;
  montoLabel: string;
}

interface ProductoPymio {
  /** Nombre en la tarjeta de selección. */
  corto: string;
  /** Rango que se muestra como tooltip de la tarjeta. */
  monto: string;
  rango: RangoMonto;
  textos?: TextosProducto;
}

export const PRODUCTOS_PYMIO: Record<ProductoPymioId, ProductoPymio> = {
  credito: {
    corto: "Crédito",
    monto: "$200 mil – $5 M",
    rango: { min: 200000, max: 5000000, step: 50000, monto: 1500000 },
  },
  credito_gar: {
    corto: "Crédito con garantía",
    monto: "$500 mil – $20 M",
    rango: { min: 500000, max: 20000000, step: 100000, monto: 5000000 },
  },
  factoraje: {
    corto: "Factoraje",
    monto: "$200 mil – $5 M",
    rango: { min: 200000, max: 5000000, step: 50000, monto: 2000000 },
    textos: {
      modo: "factoraje",
      eyebrow: "Factoraje",
      titulo: "Solicita tu línea de factoraje",
      subtitulo:
        "Línea revolvente para anticipar el cobro de tus facturas. Ajusta el monto de la línea y el plazo de las facturas.",
      montoLabel: "Monto de la línea",
    },
  },
  tpv: {
    corto: "Adelanto TPV",
    monto: "$200 mil – $10 M",
    rango: { min: 200000, max: 10000000, step: 50000, monto: 1000000 },
    textos: {
      modo: "tpv",
      eyebrow: "Adelanto por ventas",
      titulo: "Estima tu adelanto sobre ventas",
      subtitulo: "Indica cuánto vendes al mes con terminal para estimar el adelanto y la retención de cada cobro.",
      montoLabel: "Ventas mensuales con terminal",
    },
  },
  arrendamiento: {
    corto: "Arrendamiento",
    monto: "$200 mil – $10 M",
    rango: { min: 200000, max: 10000000, step: 50000, monto: 2000000 },
  },
};

interface FlujoPymio {
  /** Label de la tarjeta "¿Qué buscas para tu empresa?". */
  pregunta: string;
  resumen: string;
  /** Productos que se pueden pedir en este flujo, en el orden en que se muestran. */
  productos: ProductoPymioId[];
}

export const FLUJOS_PYMIO: Record<FlujoPymioId, FlujoPymio> = {
  liquidez: {
    pregunta: "Liquidez",
    resumen: "Capital para operar y crecer",
    productos: ["credito", "credito_gar", "factoraje", "tpv"],
  },
  activo: {
    pregunta: "Un activo",
    resumen: "Vehículo, maquinaria o equipo",
    productos: ["arrendamiento", "credito"],
  },
};

/** Plazos del crédito y del arrendamiento, en meses. */
export const PLAZOS_MESES_PYMIO = [12, 24, 36, 48, 60];
/** Plazos de las facturas (factoraje), en días. */
export const PLAZOS_FACTURAS_PYMIO = [30, 60, 90, 120, 180];

/**
 * Tasas de referencia del prototipo: 22% anual en arrendamiento (promedio de las financieras del marketplace) y
 * 35% en lo demás. Es una estimación de solicitud, no una oferta.
 */
export const TASA_ARRENDAMIENTO_PYMIO = 0.22;
export const TASA_CREDITO_PYMIO = 0.35;

export interface CotizacionPymio extends RangoMonto {
  modo: ModoCotizadorPymio;
  eyebrow: string;
  titulo: string;
  subtitulo: string;
  montoLabel: string;
  /** Se pidió crédito con garantía: se pregunta el tipo de garantía. */
  conGarantia: boolean;
  /** Se pidió arrendamiento (flujo de activo): se agrega el valor residual. */
  conArrendamiento: boolean;
  /** Se pidió factoraje junto con otros productos: el plazo de las facturas va aparte del plazo del crédito. */
  conFactoraje: boolean;
}

/**
 * Arma el cotizador para los productos pedidos (`pymConstruirFlujo` del prototipo): el rango cubre a todos y
 * los textos son los del producto cuando es uno solo con textos propios (factoraje, TPV).
 */
export const armarCotizacionPymio = (flujoId: FlujoPymioId, seleccion: ProductoPymioId[]): CotizacionPymio => {
  const productos = seleccion.map((id) => PRODUCTOS_PYMIO[id]);
  const rangos = productos.map((p) => p.rango);
  const textos = productos.length === 1 ? productos[0].textos : undefined;
  const activo = flujoId === "activo";
  const modo: ModoCotizadorPymio = activo ? "activo" : (textos?.modo ?? "liquidez");

  return {
    min: Math.min(...rangos.map((r) => r.min)),
    max: Math.max(...rangos.map((r) => r.max)),
    step: Math.min(...rangos.map((r) => r.step)),
    monto: Math.max(...rangos.map((r) => r.monto)),
    modo,
    eyebrow: textos?.eyebrow ?? "Tu solicitud",
    titulo: textos?.titulo ?? "Estima tu financiamiento",
    subtitulo:
      textos?.subtitulo ??
      (activo
        ? "Ajusta el valor del activo y el plazo. Las financieras te ofertarán arrendamiento, crédito o ambos."
        : "Ajusta monto y plazo. Cada financiera te hará una oferta por los productos que seleccionaste."),
    montoLabel: activo ? "Valor del activo" : (textos?.montoLabel ?? "¿Cuánto necesitas?"),
    conGarantia: seleccion.includes("credito_gar"),
    conArrendamiento: activo && seleccion.includes("arrendamiento"),
    conFactoraje: modo !== "factoraje" && seleccion.includes("factoraje"),
  };
};
